import { useState, useCallback, useEffect } from 'react'
import { Search as SearchIcon, FileText, Users, Calendar, Folder, X } from 'lucide-react'
import { useDebounce } from '../../hooks/useDebounce'

type SearchResultType = 'case' | 'document' | 'user' | 'deadline' | 'workspace'

interface SearchResult {
  id: string
  type: SearchResultType
  title: string
  description?: string
  metadata?: Record<string, string>
  link: string
}

const Search = () => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [recentSearches, setRecentSearches] = useState<string[]>([
    'Smith vs Johnson',
    'Contract draft',
    'John Doe',
    'Hearing next week',
  ])
  const debouncedQuery = useDebounce(query, 300)

  const searchAll = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([])
      return
    }
    setIsLoading(true)
    try {
      // TODO: Replace with real API endpoint
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 500))
      const mockResults: SearchResult[] = [
        {
          id: '1',
          type: 'case',
          title: 'Smith vs Johnson',
          description: 'Civil litigation - Discovery phase',
          metadata: { caseNumber: 'CIV-2024-001', status: 'Active' },
          link: '/cases/1',
        },
        {
          id: '2',
          type: 'document',
          title: 'Contract draft v3.docx',
          description: 'Last modified 2 hours ago by Jane Smith',
          metadata: { size: '2.4 MB', version: '3' },
          link: '/documents/2',
        },
        {
          id: '3',
          type: 'user',
          title: 'John Doe',
          description: 'Senior Lawyer - Corporate Practice',
          metadata: { email: 'john.doe@firm.com', role: 'Lawyer' },
          link: '/profile/3',
        },
        {
          id: '4',
          type: 'deadline',
          title: 'Hearing preparation deadline',
          description: 'Due in 3 days for Smith vs Johnson',
          metadata: { dueDate: '2024-12-15', priority: 'High' },
          link: '/deadlines/4',
        },
        {
          id: '5',
          type: 'workspace',
          title: 'Corporate Mergers',
          description: 'Workspace for all merger-related documents',
          metadata: { files: '42', lastActive: 'Yesterday' },
          link: '/workspaces/5',
        },
      ]
      setResults(mockResults.filter(r =>
        r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.description?.toLowerCase().includes(searchQuery.toLowerCase())
      ))
    } catch (error) {
      console.error('Search failed:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    searchAll(debouncedQuery)
  }, [debouncedQuery, searchAll])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      setRecentSearches(prev => [query, ...prev.filter(s => s !== query).slice(0, 4)])
      searchAll(query)
    }
  }

  const clearSearch = () => {
    setQuery('')
    setResults([])
  }

  const getIcon = (type: SearchResultType) => {
    switch (type) {
      case 'case': return <FileText className="w-5 h-5 text-primary-600" />
      case 'document': return <FileText className="w-5 h-5 text-blue-600" />
      case 'user': return <Users className="w-5 h-5 text-green-600" />
      case 'deadline': return <Calendar className="w-5 h-5 text-red-600" />
      case 'workspace': return <Folder className="w-5 h-5 text-purple-600" />
      default: return <SearchIcon className="w-5 h-5 text-gray-600" />
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Global Search</h1>
        <p className="text-gray-600">Search across cases, documents, people, deadlines, and workspaces</p>
      </div>

      <form onSubmit={handleSearch} className="mb-8">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-12 py-3 text-lg border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Type to search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={clearSearch}
            >
              <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-sm text-gray-500">Quick filters:</span>
          <button
            type="button"
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full"
            onClick={() => setQuery('case:')}
          >
            Cases
          </button>
          <button
            type="button"
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full"
            onClick={() => setQuery('document:')}
          >
            Documents
          </button>
          <button
            type="button"
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full"
            onClick={() => setQuery('deadline:')}
          >
            Deadlines
          </button>
          <button
            type="button"
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full"
            onClick={() => setQuery('user:')}
          >
            People
          </button>
        </div>
      </form>

      {isLoading && (
        <div className="flex justify-center my-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      )}

      {!isLoading && results.length > 0 && (
        <div className="mb-10">
          <h2 className="text-xl font-semibold mb-4">Search Results ({results.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((result) => (
              <a
                key={result.id}
                href={result.link}
                className="block p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start mb-2">
                  <div className="mr-3 mt-1">{getIcon(result.type)}</div>
                  <div>
                    <div className="flex items-center">
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded capitalize mr-2">
                        {result.type}
                      </span>
                      <h3 className="text-lg font-medium text-gray-900">{result.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm mt-1">{result.description}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {result.metadata &&
                        Object.entries(result.metadata).map(([key, value]) => (
                          <span key={key} className="text-xs text-gray-500">
                            <span className="font-medium">{key}:</span> {value}
                          </span>
                        ))}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {!isLoading && query && results.length === 0 && (
        <div className="text-center py-12">
          <SearchIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No results found</h3>
          <p className="mt-2 text-gray-500">Try different keywords or filters</p>
        </div>
      )}

      {!query && (
        <div>
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Recent Searches</h2>
            <div className="flex flex-wrap gap-2">
              {recentSearches.map((term, idx) => (
                <button
                  key={idx}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm"
                  onClick={() => setQuery(term)}
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl">
              <FileText className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Search Cases</h3>
              <p className="text-gray-600">Find cases by name, client, status, or case number.</p>
            </div>
            <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
              <FileText className="h-10 w-10 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Search Documents</h3>
              <p className="text-gray-600">Full‑text search across all documents and versions.</p>
            </div>
            <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
              <Users className="h-10 w-10 text-green-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Search People</h3>
              <p className="text-gray-600">Find colleagues by name, role, or department.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Search